import {computed, inject, Injectable, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {UserService} from '@innobile/authmodule';
import moment, {Moment, MomentInput} from 'moment';
import {of} from 'rxjs';
import {CompanyUser} from '../../models/CompanyUser';
import {nowInTimezone} from '../timezone';
import {BackendDateFormats, DateFormatKey, DateFormatProfile, isUsTimezone} from './date-format';
import {CHART_DATE_FORMATS, DATE_FORMATS, PERIOD_DATE_FORMATS, WITHOUT_YEAR} from './date-format.constants';

/**
 * Which display format the app is in, derived from the selected company's timezone: US zones get
 * the US format (`Sep 4 2026 3:42 pm`) in every build, everything else — including several
 * companies selected at once — keeps the default (`2026.09.04 15:42`).
 *
 * Switching company reloads the page, so the profile is fixed for a page's lifetime once a user is
 * signed in; that is what lets pure pipes read it. Without a `UserService` at all — a shared control
 * rendered outside the app shell — the default profile applies.
 */
@Injectable({providedIn: 'root'})
export class DateFormatService {
  private readonly user = toSignal(inject(UserService, {optional: true})?.userSubject ?? of(null), {requireSync: true});

  /** The selected company's IANA zone, or `null` (none selected, several, or no zone on it). */
  readonly timezone: Signal<string | null> = computed(() =>
    (this.user() as CompanyUser | null)?.SelectedTimezone ?? null);
  readonly profile: Signal<DateFormatProfile> = computed(() => isUsTimezone(this.timezone()) ? 'us' : 'default');
  readonly is12h = computed(() => this.profile() === 'us');

  /**
   * "Now" as the company zone's wall clock — what a default window, a "today" bound or a `maxDate`
   * should be built from, since the backend reads dates in that zone and it can be on another
   * calendar day than the browser. The browser clock without a single company zone.
   */
  now(): Moment {
    return nowInTimezone(this.timezone());
  }

  /** The moment pattern for `key` in the current profile. */
  pattern(key: DateFormatKey): string {
    return DATE_FORMATS[this.profile()][key];
  }

  /** The amCharts pattern for `key` in the current profile. */
  chartPattern(key: DateFormatKey): string {
    return CHART_DATE_FORMATS[this.profile()][key];
  }

  /** `value` in the current profile's `key` format, or `''` when it is not a valid date. */
  format(value: MomentInput, key: DateFormatKey): string {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format(this.pattern(key)) : '';
  }

  /**
   * A date shown on its own, as the tables and the period selector show one: the US format drops the
   * year inside the company zone's current year (`Sep 16 3:42 pm`) and keeps it outside
   * (`Sep 16 2025 3:42 pm`); the default always shows it (`2026.09.16 15:42`). `''` when `value`
   * is not a valid date.
   */
  formatOmittingCurrentYear(value: MomentInput, key: 'date' | 'dateTime'): string {
    const parsed = moment(value);
    if (!parsed.isValid()) {
      return '';
    }
    const omitYear = this.profile() === 'us' && parsed.year() === this.now().year();
    return parsed.format(this.pattern(omitYear ? WITHOUT_YEAR[key] : key));
  }

  /**
   * A stretch's start and end times, `06:38-19:10` by default. The US format names a shared am/pm
   * once (`6:38-7:10 pm`) and on both ends when they differ (`11:40 am-12:20 pm`). `''` when either
   * end is not a valid date.
   */
  formatTimeRange(start: MomentInput, end: MomentInput): string {
    const from = moment(start);
    const to = moment(end);
    if (!from.isValid() || !to.isValid()) {
      return '';
    }
    const endTime = to.format(this.pattern('time'));
    if (this.is12h() && from.format('a') === to.format('a')) {
      // `h:mm`: the `time` key's figures without its am/pm, which the end already carries.
      return `${from.format('h:mm')}-${endTime}`;
    }
    return `${from.format(this.pattern('time'))}-${endTime}`;
  }

  /**
   * An hour-long window on one day, the date said once: `09.24 00-01` by default. The US format
   * names am/pm once when both ends share it (`Sep 24 12-1 am`) and on both ends when they differ
   * (`Sep 24 11 am-12 pm`). `''` when either end is not a valid date.
   */
  formatHourRange(start: MomentInput, end: MomentInput): string {
    const from = moment(start);
    const to = moment(end);
    if (!from.isValid() || !to.isValid()) {
      return '';
    }
    const endHour = to.format(this.pattern('hour'));
    if (this.is12h() && from.format('a') === to.format('a')) {
      // `h`: the `hour` key's number without its am/pm, which the end already carries.
      return `${from.format(this.pattern('shortDate'))} ${from.format('h')}-${endHour}`;
    }
    return `${from.format(this.pattern('shortDateHour'))}-${endHour}`;
  }

  /**
   * A date string the backend formatted itself, re-shown in the current profile: parsed strictly
   * against each of `formats`' exact moment formats and shown in the key it maps to. Read as the
   * wall-clock time it states — no zone is applied or converted. Anything that matches none of them
   * (`n/a`, a format the backend has since changed) is shown as it arrived.
   */
  reformat(value: string | null | undefined, formats: BackendDateFormats): string {
    if (!value) {
      return value ?? '';
    }
    for (const [format, key] of Object.entries(formats)) {
      const parsed = moment(value, format, true);
      if (parsed.isValid()) {
        return parsed.format(this.pattern(key));
      }
    }
    return value;
  }

  /**
   * A period-selector9 date field: the default `2026.09.16`; in the US format `Sep 16` inside the
   * company zone's current year and `Dec 31, 2025` outside it.
   */
  formatPeriodDate(date: Moment): string {
    if (this.profile() === 'default') {
      return date.format(PERIOD_DATE_FORMATS.default);
    }
    const currentYear = nowInTimezone(this.timezone()).year();
    return date.format(date.year() === currentYear ? PERIOD_DATE_FORMATS.usCurrentYear : PERIOD_DATE_FORMATS.usOtherYear);
  }
}
